import React, { useState, useCallback } from 'react';
import { axiosInstance } from '../../utils/axios';
import './doctor.scss';

// Render の全サブドメインではなく、Medichat API の既知ホストのみ許可する
const ALLOWED_DOCTOR_LOGIN_HOSTS = new Set([
  'api.curenow.jp',
  'api.medichat.health',
  'medichat-api.onrender.com',
  'medichat-api-stg.onrender.com',
  'localhost',
  '127.0.0.1',
]);

const isAllowedDoctorLoginUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    if (!parsed.pathname.startsWith('/doctor/login')) return false;
    // 相対URL（同一オリジン）または明示的な API/バックエンドホストのみ許可
    if (parsed.origin === window.location.origin) return true;
    return ALLOWED_DOCTOR_LOGIN_HOSTS.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
};

/**
 * 医師アカウント連携画面
 * - 主導線: Medichat のメール/パスワードで既存アカウントに LINE を紐付け
 * - 補助: 招待コードでの登録/連携
 */
const DoctorInviteCodePage = ({
  lineUid,
  mode = 'register',
  message,
  loginUrl,
  onSuccess,
  onRetry,
}) => {
  const [inviteCode, setInviteCode] = useState('MC-');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const safeLoginUrl = isAllowedDoctorLoginUrl(loginUrl) ? loginUrl : null;
  const [showInviteForm, setShowInviteForm] = useState(!safeLoginUrl);
  const isLineLinkMode = mode === 'line-link';

  const handleChange = useCallback((e) => {
    let value = e.target.value.toUpperCase();

    if (!value.startsWith('MC-')) {
      value = 'MC-' + value.replace(/^MC-?/i, '');
    }

    value = value.replace(/[^A-Z0-9-]/g, '').slice(0, 10);
    setInviteCode(value);
  }, []);

  const handlePasswordLink = () => {
    if (!safeLoginUrl) {
      setError('連携用URLが不正です。最初からやり直してください。');
      return;
    }
    window.location.assign(safeLoginUrl);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const code = inviteCode.trim();

    if (code.length < 6) {
      setError('招待コードを正しく入力してください');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosInstance.post('/auth/liff', {
        access_token: window.liffAccessToken || 'mocked_access_token_123',
        medichat_role: 'doctor',
        invite_code: code,
        liff_id: window.liffResolvedId,
      });

      if (response.data.success) {
        if (onSuccess) {
          onSuccess(response.data);
        }
      } else {
        setError(response.data.error || '認証に失敗しました');
      }
    } catch (err) {
      console.error('[InviteCode] エラー:', err);
      const errorMessage = err.response?.data?.error || '認証に失敗しました';

      if (err.response?.data?.error_code === 'INVALID_INVITE_CODE') {
        setError('招待コードが無効です。コードを確認してください。');
      } else if (err.response?.data?.error_code === 'INVITE_CODE_ALREADY_USED') {
        setError('この招待コードは既に使用されています。');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="doctor-invite-code-page">
      <div className="invite-code-container">
        <div className="invite-code-header">
          <div className="logo">🏥 CureNow</div>
          <h1>アカウント連携</h1>
          <p className="subtitle">
            {safeLoginUrl
              ? 'CureNow（Medichat）の医師アカウントのメールアドレスとパスワードでLINEを連携してください'
              : isLineLinkMode
                ? '既存の医師アカウントにLINEを紐付けるため、招待コードを入力してください'
                : '運営から受け取った招待コードを入力してください'}
          </p>
        </div>

        {message && !error && (
          <div className="help-section">
            <p>{message}</p>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {safeLoginUrl && (
          <div className="password-link-section">
            <button
              type="button"
              className="submit-button"
              onClick={handlePasswordLink}
            >
              メールとパスワードで連携する
            </button>
            <p className="form-hint password-link-hint">
              リカレポータルの個人メールとは別です。CureNow / Medichat で使っているログイン情報を入力してください。
            </p>
          </div>
        )}

        {loginUrl && !safeLoginUrl && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            連携用URLが不正なため、パスワード連携を無効化しました。招待コードで進むか、最初からやり直してください。
          </div>
        )}

        {safeLoginUrl && !showInviteForm && (
          <div className="invite-code-alt">
            <button
              type="button"
              className="retry-button"
              onClick={() => setShowInviteForm(true)}
            >
              招待コードで進む
            </button>
          </div>
        )}

        {showInviteForm && (
          <form onSubmit={handleSubmit} className="invite-code-form">
            {safeLoginUrl && (
              <div className="invite-code-divider">
                <span>または招待コード</span>
              </div>
            )}
            <div className="form-group">
              <label htmlFor="invite_code">招待コード</label>
              <input
                type="text"
                id="invite_code"
                value={inviteCode}
                onChange={handleChange}
                placeholder="MC-XXXXXX"
                className="invite-code-input"
                autoComplete="off"
                autoCapitalize="none"
                maxLength={10}
                required
              />
              <p className="form-hint">
                例: MC-ABC123（運営から共有されたコード）
              </p>
            </div>

            <button
              type="submit"
              className="submit-button secondary-submit"
              disabled={loading || inviteCode.length < 6}
            >
              {loading ? '確認中...' : isLineLinkMode ? 'LINE連携を続ける' : '登録を始める'}
            </button>
          </form>
        )}

        <div className="help-section">
          <h3>💡 ログイン情報や招待コードが分からない場合</h3>
          <p>
            運営担当者にお問い合わせください。
            {lineUid ? (
              <>
                <br />
                （サポート用: LINE認識済み）
              </>
            ) : null}
          </p>
        </div>

        {onRetry && (
          <div className="retry-section">
            <button onClick={onRetry} className="retry-button">
              最初からやり直す
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorInviteCodePage;
